import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, FileSpreadsheet, Download } from "lucide-react";
import { toast } from "sonner";

function currentFy() {
    const t = new Date();
    const sy = t.getMonth() < 3 ? t.getFullYear() - 1 : t.getFullYear();
    return `${sy}-${String((sy + 1) % 100).padStart(2, "0")}`;
}

export default function TDS() {
    const [tab, setTab] = useState("sections");

    return (
        <>
            <PageHeader
                kicker="TAXES"
                title="TDS / TCS"
                subtitle="Section-wise tax deduction master, threshold-aware computation, and Form 26Q-ready quarterly summary."
            />
            <PageBody>
                <Tabs value={tab} onValueChange={setTab} className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="sections" data-testid="tds-tab-sections">Sections & rates</TabsTrigger>
                        <TabsTrigger value="summary" data-testid="tds-tab-summary">Quarterly summary</TabsTrigger>
                    </TabsList>
                    <TabsContent value="sections"><TDSSections /></TabsContent>
                    <TabsContent value="summary"><TDSSummary /></TabsContent>
                </Tabs>
            </PageBody>
        </>
    );
}

function TDSSections() {
    const [items, setItems] = useState([]);
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/tds/sections"); setItems(data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (s) => {
        if (!confirm(`Delete TDS section ${s.section} - ${s.name}?`)) return;
        await api.delete(`/tds/sections/${s.id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <>
            <div className="flex items-center justify-end mb-3">
                <Button size="sm" onClick={() => setDrawer({ open: true, item: null })} data-testid="tds-add-section">
                    <Plus className="w-3.5 h-3.5 mr-1.5" />New section
                </Button>
            </div>
            <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="tds-sections-table">
                {loading ? <div className="p-8 text-center text-muted-foreground">Loading…</div> : (
                    <table className="fin-table">
                        <thead><tr>
                            <th>Section</th><th>Description</th><th>Applies to</th>
                            <th className="num">Rate %</th><th className="num">Threshold</th>
                            <th></th>
                        </tr></thead>
                        <tbody>
                            {items.map((s) => (
                                <tr key={s.id} data-testid={`tds-section-${s.id}`}>
                                    <td className="font-mono text-[13px] font-semibold">{s.section}</td>
                                    <td>{s.name}</td>
                                    <td className="text-[12px] text-muted-foreground capitalize">{s.applies_to}</td>
                                    <td className="num font-mono">{s.rate.toFixed(2)}%</td>
                                    <td className="num font-mono">{fmtMoney(s.threshold)}</td>
                                    <td className="text-right">
                                        <Button variant="ghost" size="sm" className="h-7" onClick={() => setDrawer({ open: true, item: s })}>Edit</Button>
                                        <Button variant="ghost" size="sm" className="h-7 text-destructive" onClick={() => remove(s)} data-testid={`tds-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <SectionEditor
                open={drawer.open}
                onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))}
                item={drawer.item}
                onSaved={async () => { setDrawer({ open: false, item: null }); await load(); }}
            />
        </>
    );
}

function SectionEditor({ open, onOpenChange, item, onSaved }) {
    const [form, setForm] = useState({ section: "", name: "", rate: 10, threshold: 30000, applies_to: "any", active: true });
    useEffect(() => { if (open) setForm(item || { section: "", name: "", rate: 10, threshold: 30000, applies_to: "any", active: true }); }, [open, item]);

    const save = async () => {
        if (!form.section || !form.name) return toast.error("Section + name required");
        await api.post("/tds/sections", { ...form, id: item?.id });
        toast.success("Saved");
        onSaved();
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-[480px]" data-testid="tds-section-editor">
                <SheetHeader>
                    <SheetTitle>{item ? "Edit TDS section" : "New TDS section"}</SheetTitle>
                    <SheetDescription>Standard rates from Indian Income Tax Act, 1961. Threshold is the cumulative annual amount above which TDS applies.</SheetDescription>
                </SheetHeader>
                <div className="space-y-3 mt-4">
                    <div><Label>Section code</Label><Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} placeholder="e.g. 194J(a)" data-testid="tds-section-code" /></div>
                    <div><Label>Description</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Professional fees" data-testid="tds-section-name" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Rate %</Label><Input type="number" step="0.1" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="tds-section-rate" /></div>
                        <div><Label>Threshold ₹</Label><Input type="number" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} data-testid="tds-section-threshold" /></div>
                    </div>
                    <div><Label>Applies to</Label>
                        <select className="w-full border rounded-md h-9 px-2 mt-1" value={form.applies_to} onChange={(e) => setForm({ ...form, applies_to: e.target.value })}>
                            <option value="any">Any deductee</option>
                            <option value="individual">Individual / HUF</option>
                            <option value="company">Company / Firm</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2 pt-1"><Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
                    <div className="flex justify-end gap-2 pt-3 border-t border-border">
                        <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button size="sm" onClick={save} data-testid="tds-section-save">Save</Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function TDSSummary() {
    const [fy, setFy] = useState(currentFy());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const run = async () => {
        setLoading(true);
        try { const { data } = await api.get("/reports/tds-summary", { params: { fy } }); setData(data); }
        finally { setLoading(false); }
    };
    useEffect(() => { run(); /* eslint-disable-next-line */ }, [fy]);

    const downloadForm26Q = async (q) => {
        const token = localStorage.getItem("vyastra_token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/tds-summary/26q?fy=${fy}&quarter=${q}`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `Form26Q_${fy}_Q${q}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <div className="bg-card border border-border rounded-md p-4 mb-4 flex flex-wrap items-end gap-3">
                <div><Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Financial year</Label><Input value={fy} onChange={(e) => setFy(e.target.value)} className="h-9 w-[140px] mt-1" data-testid="tds-fy" /></div>
                <div className="ml-auto flex items-center gap-2">
                    {[1, 2, 3, 4].map((q) => (
                        <Button key={q} variant="outline" size="sm" onClick={() => downloadForm26Q(q)} data-testid={`tds-26q-${q}`}>
                            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Form 26Q · Q{q}
                        </Button>
                    ))}
                </div>
            </div>
            {loading && <div className="text-center text-muted-foreground py-8">Loading…</div>}
            {data && !loading && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <Kpi label="Bills with TDS" value={data.totals.bills} format="number" />
                        <Kpi label="Gross subject to TDS" value={data.totals.gross} />
                        <Kpi label="TDS deducted" value={data.totals.tds} tone="positive" />
                    </div>
                    <div className="bg-card border border-border rounded-md overflow-hidden mb-4" data-testid="tds-sections-summary">
                        <div className="px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">By section</div>
                        {data.sections.length === 0 ? <div className="p-6 text-center text-muted-foreground text-sm">No bills with TDS recorded for this FY.</div> : (
                            <table className="fin-table">
                                <thead><tr><th>Section</th><th>Description</th><th className="num">Rate %</th><th className="num">Vendors</th><th className="num">Bills</th><th className="num">Gross</th><th className="num">TDS</th></tr></thead>
                                <tbody>
                                    {data.sections.map((s) => (
                                        <tr key={s.section}>
                                            <td className="font-mono font-semibold">{s.section}</td>
                                            <td className="text-[12px]">{s.section_name}</td>
                                            <td className="num font-mono">{Number(s.rate).toFixed(2)}%</td>
                                            <td className="num">{s.vendor_count}</td>
                                            <td className="num">{s.count}</td>
                                            <td className="num">{fmtMoney(s.gross)}</td>
                                            <td className="num font-medium">{fmtMoney(s.tds)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    {data.vendors.length > 0 && (
                        <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="tds-vendors-summary">
                            <div className="px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">By vendor (top deductees)</div>
                            <table className="fin-table">
                                <thead><tr><th>Vendor</th><th>PAN</th><th>Section</th><th className="num">Bills</th><th className="num">Gross</th><th className="num">TDS</th></tr></thead>
                                <tbody>
                                    {data.vendors.slice(0, 50).map((v, i) => (
                                        <tr key={i}>
                                            <td className="font-medium">{v.vendor_name || "—"}</td>
                                            <td className="font-mono text-[12px]">{v.vendor_pan || "—"}</td>
                                            <td className="font-mono">{v.section}</td>
                                            <td className="num">{v.count}</td>
                                            <td className="num">{fmtMoney(v.gross)}</td>
                                            <td className="num font-medium">{fmtMoney(v.tds)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </>
    );
}

function Kpi({ label, value, format = "money", tone }) {
    const formatted = format === "money" ? fmtMoney(value || 0) : (value ?? 0);
    const toneCls = tone === "positive" ? "text-positive" : "";
    return (
        <div className="bg-card border border-border rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">{label}</div>
            <div className={`font-display text-2xl font-light tracking-tight mt-1 ${toneCls}`}>{formatted}</div>
        </div>
    );
}
